// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import { dispatch } from '../index';

import { getCustomRangeCareStatsForLoggedInUser, getMonthlyInteractionsForLoggedInUser } from '../../api/productivity';
import { setLastUsedClinic } from '../../utils/clinicPersistence';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    loading: true,
    email: null,
    userId: 0,
    userMetadata: {
        isProviderAccount: false,
        providerId: null,
        firstName: '',
        lastName: ''
    },
    /*
    clinicGrants: {
        1: {
            meta: {
                name: 'Clinic 1',
            },
            grants: {
                CLINIC_MANAGER: true,
            }
            ...
        },
        ...
    }
    organizationMeta: {
        meta: {
            id: 1,
            name: 'SynerGrx'
        }
        grants: {
            ORG_USER: true
        }
        ...
    ...
    }
    */
    clinicGrants: {},
    organizationMeta: {},
    // string id --> default to 0 to avoid writing non numbers for replacing id
    selectedClinic: '0',
    customRangeProviderCareStats: {
        loaded: false,
        error: false,
        needsRefresh: false,
        custom: {
            secondsLogged: 0,
            weeklyGoalHours: 0
        }
    },
    monthlyInteractions: {
        loaded: false,
        error: false,
        month: {
            patientsCaredFor: 0
        }
    }
};

const slice = createSlice({
    name: 'account',
    initialState,
    reducers: {
        setAccount(state, action) {
            state.loading = false;
            state.email = action.payload.email;
            state.userId = action.payload.userId;
            state.clinicGrants = action.payload.clinicGrants;
            state.organizationMeta = action.payload.organizationMeta;
            state.userMetadata = action.payload.userMetadata;
        },
        setClinic(state, action) {
            state.selectedClinic = action.payload;
        },

        setMonthlyInteractions(state, action) {
            state.monthlyInteractions.loaded = true;
            state.monthlyInteractions.error = false;
            state.monthlyInteractions.month = action.payload.month;
        },
        setMonthlyInteractionsError(state, action) {
            state.monthlyInteractions.loaded = true;
            state.monthlyInteractions.error = action.payload;
        },

        setCustomRangeProviderCareStats(state, action) {
            state.customRangeProviderCareStats.loaded = true;
            state.customRangeProviderCareStats.error = false;
            state.customRangeProviderCareStats.needsRefresh = false;
            state.customRangeProviderCareStats.custom = action.payload.custom;
        },
        setCustomRangeProviderCareStatsLoading(state) {
            state.customRangeProviderCareStats.loaded = false;
            state.customRangeProviderCareStats.error = false;
            state.customRangeProviderCareStats.needsRefresh = false;
        },
        setCustomRangeProviderCareStatsError(state, action) {
            state.customRangeProviderCareStats.loaded = true;
            state.customRangeProviderCareStats.needsRefresh = false;
            state.customRangeProviderCareStats.error = action.payload;
        },
        setCustomRangeProviderCareStatsNeedsRefresh(state) {
            state.customRangeProviderCareStats.needsRefresh = true;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function setAccountData(email, userId, clinicGrants, organizationMeta, userMetadata) {
    return async () => {
        dispatch(
            slice.actions.setAccount({
                userId,
                email,
                clinicGrants,
                organizationMeta,
                userMetadata
            })
        );
    };
}

export function setClinic(clinic) {
    return async () => {
        dispatch(slice.actions.setClinic(clinic));
        setLastUsedClinic(clinic);
    };
}

export function getMonthlyInteractions() {
    return async () => {
        const response = await getMonthlyInteractionsForLoggedInUser();
        if (response.status === 200) {
            dispatch(slice.actions.setMonthlyInteractions(response.data));
        } else {
            dispatch(slice.actions.setMonthlyInteractionsError(response.message));
        }
    };
}

export function getCustomRangeProviderCareStats(fromTS, toTS, weekStartDate) {
    return async () => {
        dispatch(slice.actions.setCustomRangeProviderCareStatsLoading());
        const response = await getCustomRangeCareStatsForLoggedInUser(fromTS, toTS, weekStartDate);
        if (response.status === 200) {
            dispatch(slice.actions.setCustomRangeProviderCareStats(response.data));
        } else {
            dispatch(slice.actions.setCustomRangeProviderCareStatsError(response.message));
        }
    };
}

export function markRefreshNeededOfCustomProviderCareStats() {
    dispatch(slice.actions.setCustomRangeProviderCareStatsNeedsRefresh());
}
